import { Text } from '@thanx/uikit/text'
import { useStyletron } from '@thanx/uikit/theme'
import { alert } from 'actions/flash'
import { pointsApi, PointsExpiration } from 'api/points'
import { getErrorMessage, isFetchBaseQueryError } from 'api/utils'
import ConfirmModal from 'components/ConfirmModal'
import useDispatch from 'hooks/useDispatch'
import { buildTranslate } from 'locales'
import moment from 'moment'
import React, { useState } from 'react'
import { Button } from 'react-bootstrap'
import BodyCellContainer from 'scenes/Reports/components/Table/BodyCellContainer'
import { merchantTheme } from 'theme'
import StatusBadge from './StatusBadge'

const t = buildTranslate('users.view.activity.points_expiration')

type Props = {
  expiration: PointsExpiration
  currencyName: string
  userIdentifier: string
}

function formatDate(date: string) {
  return moment(date).format('MMM D, YYYY')
}

const Row: React.FC<Props> = ({ expiration, currencyName, userIdentifier }) => {
  const { amount, expiration_date, status, id } = expiration
  const { colors } = merchantTheme
  const [css] = useStyletron()
  const dispatch = useDispatch()
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [reverseExpiration, { isLoading }] =
    pointsApi.useReversePointsExpirationMutation()

  const cellPadding = css({
    paddingLeft: '24px',
    paddingRight: '24px',
  })

  const handleReverse = async () => {
    if (!id) return

    try {
      await reverseExpiration({ userIdentifier, transactionId: id }).unwrap()
      dispatch(
        alert({
          key: 'success',
          message: t('reverse_modal.success'),
          timeout: 5,
        })
      )
      setShowConfirmModal(false)
    } catch (error) {
      const message = isFetchBaseQueryError(error)
        ? getErrorMessage(error, t('reverse_modal.error'))
        : t('reverse_modal.error')
      dispatch(
        alert({
          key: 'danger',
          message,
          timeout: 5,
        })
      )
    }
  }

  const canReverse = status === 'expired' && id

  return (
    <>
      <tr>
        <BodyCellContainer
          isErrored={false}
          isLoading={false}
          className={cellPadding}
        >
          <Text.BodyText4 color={colors.grey70} tag="div">
            {amount.toLocaleString()} {currencyName}
          </Text.BodyText4>
        </BodyCellContainer>
        <BodyCellContainer
          isErrored={false}
          isLoading={false}
          className={cellPadding}
        >
          <Text.BodyText4 color={colors.grey70} tag="div">
            {formatDate(expiration_date)}
          </Text.BodyText4>
        </BodyCellContainer>
        <BodyCellContainer
          isErrored={false}
          isLoading={false}
          className={cellPadding}
        >
          <StatusBadge status={status} />
        </BodyCellContainer>
        <BodyCellContainer
          isErrored={false}
          isLoading={false}
          className={cellPadding}
        >
          {canReverse && (
            <Button
              bsStyle="link"
              className="p-0"
              onClick={() => setShowConfirmModal(true)}
            >
              {t('actions.reverse')}
            </Button>
          )}
        </BodyCellContainer>
      </tr>
      <ConfirmModal
        isOpen={showConfirmModal}
        isLoading={isLoading}
        title={t('reverse_modal.title')}
        description={t('reverse_modal.description', {
          amount: amount.toLocaleString(),
        })}
        confirmText={t('reverse_modal.confirm')}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={handleReverse}
        isJustified={false}
      />
    </>
  )
}

export default Row
